import clsx from 'clsx'
import { orderBy, upperFirst } from 'lodash-es'
import type { PropsWithChildren } from 'react'
import { useToggle } from 'usehooks-ts'

import { FvButton } from '@fv/client-components'
import type { ListShipment } from '@fv/client-types'
import type { TrackDateTime } from '@fv/models'
import { getTrackEventDate } from '@fv/models/core'

import { List, ListItem } from '@/components/List'

import { useTracking } from './queries'
import { formatTrackingDateTime } from './utils'

const LIMITED_COUNT = 5

const ListWrapper = ({ children }: PropsWithChildren) => (
  <List fcp0 lcp0 className="flex-1">
    {children}
  </List>
)

type Props = {
  load: ListShipment
  isCollapsed: boolean
}
export const TrackHistoryList = ({ load, isCollapsed }: Props) => {
  const [isShowingAll, toggleShowingAll] = useToggle(false)
  const isQueryEnabled = !isCollapsed
  const trackingQuery = useTracking(load._id, isQueryEnabled)

  if (isCollapsed || !trackingQuery.data?.trackEvents?.length) {
    return (
      <ListWrapper>
        <TrackHistoryListItem
          dateTime={load.tracking.currentLocation?.eventDateTime}
          summary={load.tracking.currentLocation?.summary}
        />
      </ListWrapper>
    )
  }

  const count = isShowingAll
    ? (trackingQuery.data?.trackEvents?.length ?? 0)
    : LIMITED_COUNT
  const events = orderBy(
    trackingQuery.data?.trackEvents ?? [],
    e => getTrackEventDate(e.eventDateTime).toDate(),
    ['desc'],
  )

  return (
    <ListWrapper>
      {events.slice(0, count).map((e, i) => (
        <TrackHistoryListItem
          key={i}
          dateTime={e.eventDateTime}
          summary={e.summary}
          lg={events.some(e => e.eventDateTime.isUTC)} // consistent size if we're showing timezone abbreviation
        />
      ))}
      {!isCollapsed && events.length > LIMITED_COUNT && !isShowingAll && (
        <ListItem itemsStart dashed>
          <FvButton
            theme="underlined"
            icon="arrow-down"
            onClick={toggleShowingAll}
          >
            Show full history
          </FvButton>
        </ListItem>
      )}
    </ListWrapper>
  )
}

type ItemProps = {
  dateTime: TrackDateTime
  summary: string
  lg?: boolean
}
const TrackHistoryListItem = ({ dateTime, summary, lg }: ItemProps) => {
  return (
    <ListItem itemsStart dashed>
      {dateTime.date && (
        <div
          className={clsx(
            'whitespace-nowrap text-sm bg-fv-beer-xlight border border-fv-beer-medium px-2 py-1 rounded-xs text-left italic leading-none -mt-[.05rem]',
            lg && 'w-44',
            !lg && 'w-42',
          )}
        >
          {formatTrackingDateTime(dateTime, {
            dateFormat: 'MM/DD/YY',
            timeFormat: 'h:mm A',
            includeTimezone: true,
          })}
        </div>
      )}
      <p className={clsx('mb-0 ml-2')}>{upperFirst(summary)}</p>
    </ListItem>
  )
}
